/**
 * The split view's code editor (TikZ step 3, 1a): CodeMirror, as the rest of
 * the editor's source surfaces use, with TikZ highlighting, a swatch before
 * each theme colour name, and a figure's error shown at its line.
 *
 * Highlighting: commands in --lilia-primary-dark at 600, maths in italic, node
 * names (`(A)`, `(start)`) in the secondary text colour, comments muted, and
 * `lilia-` names at 600 with their swatch.
 */
import { RangeSet, StateEffect, StateField, type EditorState, type Extension, type Text } from "@codemirror/state";
import {
  Decoration,
  EditorView,
  GutterMarker,
  ViewPlugin,
  WidgetType,
  gutterLineClass,
  type DecorationSet,
  type ViewUpdate,
} from "@codemirror/view";
import { HighlightStyle, StreamLanguage, syntaxHighlighting, type StringStream } from "@codemirror/language";
import { tags } from "@lezer/highlight";
import { testIds } from "@/testIds";
import type { FigureError } from "@/lib/tikzFigure";
import type { FigureColours } from "@/lib/tikzAuthoring";

/** The one change that turns `a` into `b`: the differing middle, or null when equal. */
export function minimalChange(a: string, b: string): { from: number; to: number; insert: string } | null {
  if (a === b) return null;
  let start = 0;
  const max = Math.min(a.length, b.length);
  while (start < max && a.charCodeAt(start) === b.charCodeAt(start)) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a.charCodeAt(endA - 1) === b.charCodeAt(endB - 1)) {
    endA--;
    endB--;
  }
  return { from: start, to: endA, insert: b.slice(start, endB) };
}

// The language ------------------------------------------------------------------------

const isWordChar = (c: string | undefined) => !!c && /[\w-]/.test(c);

export const tikzLanguage = StreamLanguage.define<null>({
  name: "tikz",
  startState: () => null,
  token(stream: StringStream): string | null {
    if (stream.match("%")) {
      stream.skipToEnd();
      return "comment";
    }
    // Maths, delimiters included, as one italic run (on one line).
    if (stream.match(/^\$[^$]*\$?/)) return "math";
    if (stream.match(/^\\[a-zA-Z@]+/) || stream.match(/^\\./)) return "command";
    // A node's name: `(A)`, `(start)`, `(A.north)`; never a coordinate like (0,0).
    if (stream.match(/^\([A-Za-z][\w.:-]*\)/)) return "nodeName";
    if (!isWordChar(stream.string[stream.pos - 1]) && stream.match(/^lilia-[\w-]+/)) return "colourName";
    if (stream.eatWhile(/[\w-]/)) return null;
    stream.next();
    return null;
  },
  tokenTable: {
    comment: tags.comment,
    math: tags.emphasis,
    command: tags.keyword,
    nodeName: tags.labelName,
    colourName: tags.color,
  },
  languageData: { commentTokens: { line: "%" } },
});

export const tikzHighlightStyle = HighlightStyle.define([
  { tag: tags.keyword, color: "var(--lilia-primary-dark)", fontWeight: "600" },
  { tag: tags.emphasis, fontStyle: "italic" },
  { tag: tags.labelName, color: "var(--lilia-text-secondary)" },
  { tag: tags.comment, color: "var(--lilia-text-secondary)", fontStyle: "italic" },
  { tag: tags.color, fontWeight: "600" },
]);

export const tikzEditorTheme = EditorView.theme({
  "&": {
    height: "100%",
    backgroundColor: "var(--lilia-bg)",
    color: "var(--lilia-text)",
    fontSize: "12.5px",
    fontFamily: "var(--lilia-font-mono)",
  },
  "&.cm-focused": { outline: "none" },
  ".cm-scroller": { overflow: "auto", lineHeight: "24px", fontFamily: "var(--lilia-font-mono)" },
  ".cm-content": { padding: "12px 0", caretColor: "var(--lilia-text)" },
  ".cm-line": { padding: "0 12px 0 16px" },
  ".cm-gutters": {
    backgroundColor: "var(--lilia-bg)",
    color: "var(--lilia-text-secondary)",
    border: "none",
  },
  ".cm-lineNumbers .cm-gutterElement": { minWidth: "44px", padding: "0 8px 0 0", fontSize: "12px", textAlign: "right" },
  ".cm-activeLine": { backgroundColor: "color-mix(in srgb, var(--lilia-primary) 4%, transparent)" },
  ".cm-activeLineGutter": { backgroundColor: "transparent", color: "var(--lilia-text)" },
  "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection": {
    backgroundColor: "color-mix(in srgb, var(--lilia-primary) 22%, transparent)",
  },
  // The swatch before a theme colour name.
  ".cm-tikz-swatch": {
    display: "inline-block",
    width: "10px",
    height: "10px",
    borderRadius: "2px",
    margin: "0 4px 0 1px",
    verticalAlign: "-1px",
    boxShadow: "0 0 0 1px rgba(0,0,0,.15)",
  },
  // An error at its line: the gutter number in danger with a 2 px edge, the
  // token underlined, the message under the line.
  ".cm-tikz-error-line": { backgroundColor: "color-mix(in srgb, var(--lilia-danger) 6%, transparent)" },
  ".cm-tikz-ai-line": { backgroundColor: "var(--lilia-primary-subtle)" },
  ".cm-gutterElement.cm-tikz-error-gutter": {
    color: "var(--lilia-danger)",
    fontWeight: "700",
    boxShadow: "inset 2px 0 0 var(--lilia-danger)",
    backgroundColor: "color-mix(in srgb, var(--lilia-danger) 6%, transparent)",
  },
  ".cm-tikz-error-token": {
    textDecoration: "underline wavy var(--lilia-danger)",
    textUnderlineOffset: "4px",
    textDecorationSkipInk: "none",
  },
  ".cm-tikz-error-inline": {
    margin: "4px 14px 6px 16px",
    padding: "9px 12px",
    borderRadius: "8px",
    background: "color-mix(in srgb, var(--lilia-danger) 7%, var(--lilia-bg))",
    border: "1px solid color-mix(in srgb, var(--lilia-danger) 45%, transparent)",
    fontFamily: "var(--lilia-font-sans)",
    fontSize: "12.5px",
    lineHeight: "1.5",
    color: "var(--lilia-text)",
    display: "flex",
    gap: "9px",
    whiteSpace: "normal",
  },
  ".cm-tikz-error-inline-bang": { color: "var(--lilia-danger)", fontWeight: "800" },
  ".cm-tikz-error-inline-note": { color: "var(--lilia-text-secondary)" },
});

export const tikzHighlighting: Extension = [tikzLanguage, syntaxHighlighting(tikzHighlightStyle)];

// Colour swatches ------------------------------------------------------------------------

export const setColoursEffect = StateEffect.define<FigureColours>();

export const coloursField = StateField.define<FigureColours>({
  create: () => ({}),
  update(value, tr) {
    for (const e of tr.effects) if (e.is(setColoursEffect)) return e.value;
    return value;
  },
});

class SwatchWidget extends WidgetType {
  constructor(readonly name: string, readonly colour: string) {
    super();
  }
  eq(other: SwatchWidget) {
    return other.colour === this.colour && other.name === this.name;
  }
  toDOM() {
    const s = document.createElement("span");
    s.className = "cm-tikz-swatch";
    s.style.background = this.colour;
    s.setAttribute("data-colour-name", this.name);
    s.setAttribute("data-colour", this.colour);
    s.setAttribute("aria-hidden", "true");
    return s;
  }
  ignoreEvent() {
    return true;
  }
}

const COLOUR_NAME = /(?<![\w-])lilia-[\w-]+/g;

function swatches(view: EditorView): DecorationSet {
  const colours = view.state.field(coloursField);
  const ranges = [];
  for (const { from, to } of view.visibleRanges) {
    const text = view.state.doc.sliceString(from, to);
    for (const m of text.matchAll(COLOUR_NAME)) {
      const colour = colours[m[0]];
      if (!colour) continue;
      ranges.push(Decoration.widget({ widget: new SwatchWidget(m[0], colour), side: -1 }).range(from + m.index!));
    }
  }
  return Decoration.set(ranges, true);
}

export const swatchPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = swatches(view);
    }
    update(u: ViewUpdate) {
      if (u.docChanged || u.viewportChanged || u.startState.field(coloursField) !== u.state.field(coloursField)) {
        this.decorations = swatches(u.view);
      }
    }
  },
  { decorations: (v) => v.decorations },
);

// The error at its line --------------------------------------------------------------------

export interface InlineErrorSpec {
  line: number;
  excerpt: string | null;
  column: FigureError["column"];
  message: string;
  /** Said after the message, e.g. that the drawing is the last version that drew. */
  note: string | null;
}

interface InlineError {
  lineFrom: number;
  from: number;
  to: number;
  message: string;
  note: string | null;
}

export const setInlineErrorEffect = StateEffect.define<InlineErrorSpec | null>();

/** Where the error sits in this text: the line, and the bad part of it. */
export function locateError(doc: Text, spec: InlineErrorSpec): InlineError {
  const n = Math.min(Math.max(1, spec.line), doc.lines);
  const line = doc.line(n);
  let from: number;
  let to: number;
  if (spec.column) {
    // The column is in the excerpt; the excerpt is normally the line itself.
    const shift = spec.excerpt ? Math.max(0, line.text.indexOf(spec.excerpt)) : 0;
    from = line.from + Math.min(line.length, shift + spec.column.start);
    to = line.from + Math.min(line.length, shift + spec.column.end);
  } else {
    const lead = line.text.length - line.text.trimStart().length;
    from = line.from + lead;
    to = line.to;
  }
  return { lineFrom: line.from, from, to: Math.max(from, to), message: spec.message, note: spec.note };
}

class ErrorWidget extends WidgetType {
  constructor(readonly message: string, readonly note: string | null) {
    super();
  }
  eq(other: ErrorWidget) {
    return other.message === this.message && other.note === this.note;
  }
  toDOM() {
    const box = document.createElement("div");
    box.className = "cm-tikz-error-inline";
    box.setAttribute("data-testid", testIds.tikzSplit.errorInline);
    box.setAttribute("role", "status");
    const bang = document.createElement("span");
    bang.className = "cm-tikz-error-inline-bang";
    bang.textContent = "!";
    const text = document.createElement("span");
    const b = document.createElement("b");
    b.textContent = this.message;
    text.appendChild(b);
    if (this.note) {
      const note = document.createElement("span");
      note.className = "cm-tikz-error-inline-note";
      note.textContent = ` ${this.note}`;
      text.appendChild(note);
    }
    box.append(bang, text);
    return box;
  }
  ignoreEvent() {
    return false;
  }
}

class ErrorGutterMarker extends GutterMarker {
  elementClass = "cm-tikz-error-gutter";
}
const errorGutter = new ErrorGutterMarker();

function errorDecorations(e: InlineError | null, doc: Text): DecorationSet {
  if (!e) return Decoration.none;
  const line = doc.lineAt(e.lineFrom);
  const ranges = [Decoration.line({ class: "cm-tikz-error-line", attributes: { "data-error-line": "true" } }).range(line.from)];
  const from = Math.min(Math.max(e.from, line.from), line.to);
  const to = Math.min(Math.max(e.to, from), line.to);
  if (to > from) {
    ranges.push(Decoration.mark({ class: "cm-tikz-error-token", attributes: { "data-error-token": "true" } }).range(from, to));
  }
  ranges.push(Decoration.widget({ widget: new ErrorWidget(e.message, e.note), block: true, side: 1 }).range(line.to));
  return Decoration.set(ranges, true);
}

/**
 * The error, mapped through every edit after it was set, so it stays on its
 * line while the author types above it; replaced by the next draw's answer.
 * Setting it never touches the selection: the cursor never moves.
 */
export const inlineErrorField = StateField.define<InlineError | null>({
  create: () => null,
  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(setInlineErrorEffect)) return e.value ? locateError(tr.state.doc, e.value) : null;
    }
    if (!value || !tr.docChanged) return value;
    return {
      ...value,
      lineFrom: tr.changes.mapPos(value.lineFrom, -1),
      from: tr.changes.mapPos(value.from, 1),
      to: tr.changes.mapPos(value.to, -1),
    };
  },
  provide: (f) => [
    EditorView.decorations.compute([f], (state: EditorState) => errorDecorations(state.field(f), state.doc)),
    gutterLineClass.compute([f], (state: EditorState) => {
      const e = state.field(f);
      return e ? RangeSet.of([errorGutter.range(state.doc.lineAt(e.lineFrom).from)]) : RangeSet.empty;
    }),
  ],
});

// Ask Lilia's line chips (1c) -----------------------------------------------------------

/** Lines `from`–`to` (1-based, inclusive) to highlight, or null. */
export const setLineHighlightEffect = StateEffect.define<{ from: number; to: number } | null>();

const aiLine = Decoration.line({ attributes: { class: "cm-tikz-ai-line", "data-ai-highlight": "true" } });

/** The lines an Explain chip points at, while it is hovered. */
export const lineHighlightField = StateField.define<{ from: number; to: number } | null>({
  create: () => null,
  update(value, tr) {
    for (const e of tr.effects) if (e.is(setLineHighlightEffect)) return e.value;
    return value;
  },
  provide: (f) =>
    EditorView.decorations.compute([f], (state: EditorState) => {
      const h = state.field(f);
      if (!h) return Decoration.none;
      const last = state.doc.lines;
      const ranges = [];
      for (let n = Math.max(1, h.from); n <= Math.min(last, h.to); n++) ranges.push(aiLine.range(state.doc.line(n).from));
      return Decoration.set(ranges);
    }),
});
